import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'localization';
import { usePublishers } from '../../../hooks/usePublishers';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import './PublisherSelectMolecule.scss';

export interface PublisherSelectMoleculeProps {
  value: string | null;
  displayName: string | null;
  onChange: (systemName: string | null, displayName: string | null) => void;
  onCreateNew: () => void;
  disabled?: boolean;
  submitAttempted?: boolean;
  className?: string;
}

const PublisherSelectMolecule: React.FC<PublisherSelectMoleculeProps> = ({
  value,
  displayName,
  onChange,
  onCreateNew,
  disabled = false,
  submitAttempted = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { publishers, loading } = usePublishers({ mine: true });

  // Click-outside handler
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const selected = value ? publishers.find((p) => p.system_name === value) : null;
  // Fallback to displayName prop when publisher not yet in the list (e.g. just created)
  const selectedName = selected?.display_name ?? displayName;
  const selectedSlug = selected?.slug ?? null;
  const selectedHue = selected?.hue;
  const selectedGlyph = selected?.glyph;
  const selectedGlyphKind = selected?.glyph_kind ?? 'letter';

  const showError = submitAttempted && !value;

  const classes = [
    'marketplace-publisher-select',
    open ? 'marketplace-publisher-select--open' : '',
    showError ? 'marketplace-publisher-select--error' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleToggle = () => {
    if (!disabled) setOpen((o) => !o);
  };

  const handleSelect = (systemName: string, pubDisplayName: string) => {
    onChange(systemName, pubDisplayName);
    setOpen(false);
  };

  const handleCreateNew = () => {
    setOpen(false);
    onCreateNew();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div
      ref={wrapperRef}
      className={classes}
      onKeyDown={handleKeyDown}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
    >
      <span className="marketplace-publisher-select__label" id="publisher-select-label">
        {t('marketplace.publishers.select.label')}
        <span className="marketplace-dialog__required" aria-hidden="true"> *</span>
      </span>

      <button
        type="button"
        className="marketplace-publisher-select__trigger"
        onClick={handleToggle}
        disabled={disabled || loading}
        aria-labelledby="publisher-select-label"
        aria-invalid={showError}
      >
        {value && selectedName ? (
          <>
            <ScopeCoinAtom
              scope="metasphere"
              glyph={selectedGlyph ?? undefined}
              channelName={selectedName}
              glyphKind={selectedGlyphKind}
              hue={selectedHue}
              size="sm"
            />
            <div className="marketplace-publisher-select__trigger-meta">
              <span className="marketplace-publisher-select__trigger-name">{selectedName}</span>
              {selectedSlug && (
                <span className="marketplace-publisher-select__trigger-slug">/{selectedSlug}</span>
              )}
            </div>
          </>
        ) : (
          <>
            <span className="marketplace-publisher-select__trigger-empty-icon" aria-hidden="true">
              <i className="pi pi-users" />
            </span>
            <span className="marketplace-publisher-select__trigger-placeholder">
              {loading
                ? t('marketplace.publishers.select.loading')
                : t('marketplace.publishers.select.placeholder')}
            </span>
          </>
        )}
        <i className="pi pi-chevron-down marketplace-publisher-select__chevron" aria-hidden="true" />
      </button>

      {open && (
        <div className="marketplace-publisher-select__dropdown" role="listbox">
          <div className="marketplace-publisher-select__list">
            {publishers.length === 0 && (
              <div className="marketplace-publisher-select__empty">
                {t('marketplace.publishers.select.noPublishers')}
              </div>
            )}
            {publishers.map((pub) => (
              <button
                key={pub.system_name}
                type="button"
                role="option"
                aria-selected={pub.system_name === value}
                className={[
                  'marketplace-publisher-select__option',
                  pub.system_name === value ? 'marketplace-publisher-select__option--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleSelect(pub.system_name, pub.display_name)}
              >
                <ScopeCoinAtom
                  scope="metasphere"
                  glyph={pub.glyph ?? undefined}
                  channelName={pub.display_name}
                  glyphKind={pub.glyph_kind ?? 'letter'}
                  hue={pub.hue}
                  size="sm"
                />
                <div className="marketplace-publisher-select__option-text">
                  <span className="marketplace-publisher-select__option-name">{pub.display_name}</span>
                  <span className="marketplace-publisher-select__option-slug">/{pub.slug}</span>
                </div>
                {pub.system_name === value && (
                  <i className="pi pi-check marketplace-publisher-select__option-check" aria-hidden="true" />
                )}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="marketplace-publisher-select__create-btn"
            onClick={handleCreateNew}
          >
            <i className="pi pi-plus-circle" aria-hidden="true" />
            {t('marketplace.publishers.select.createNew')}
          </button>
        </div>
      )}

      {showError && (
        <span className="marketplace-publisher-select__error" role="alert">
          {t('marketplace.publishers.select.required')}
        </span>
      )}
    </div>
  );
};

export default PublisherSelectMolecule;
